/**
 * @module data/trends
 *
 * The horizon radar: emerging technologies and shifts that will change how
 * enterprise architecture is practised, plotted on a standard four-ring radar
 * with an explicit "so what" for each item.
 *
 * Rings follow the familiar convention: **Adopt** (proven, act now),
 * **Trial** (act in a contained way), **Assess** (watch with intent),
 * **Hold** (do not invest yet).
 */

/**
 * @typedef {Object} Trend
 * @property {string}   id
 * @property {string}   name
 * @property {"adopt"|"trial"|"assess"|"hold"} ring
 * @property {number}   angle     Degrees, for radial placement.
 * @property {string}   quadrant  `"Data"`, `"Models"`, `"Delivery"`, `"Assurance"`.
 * @property {string}   body      What it is.
 * @property {string}   sowhat    What it means for architecture, concretely.
 * @property {string}   horizon   When it matters: `"Now"`, `"12–18 months"`, `"3+ years"`.
 * @property {string}   glyph
 * @property {string}   accent
 */

/** Ring definitions, ordered from outer (hold) to inner (adopt). */
export const RINGS = [
  { id: "adopt", label: "Adopt", radius: 0.28, body: "Proven in production. Design it in now." },
  { id: "trial", label: "Trial", radius: 0.48, body: "Worth a contained, measured experiment." },
  { id: "assess", label: "Assess", radius: 0.7, body: "Understand it; do not commit budget." },
  { id: "hold", label: "Hold", radius: 0.92, body: "Not yet. The economics or the risk do not work." },
];

/** The four radar quadrants. */
export const QUADRANTS = ["Data", "Models", "Delivery", "Assurance"];

/** @type {Trend[]} */
export const TRENDS = [
  {
    id: "semantic-contracts",
    name: "Semantic data contracts",
    ring: "adopt",
    angle: 32,
    quadrant: "Data",
    body: "Machine-readable contracts that carry meaning — units, semantics, quality thresholds — not just schema.",
    sowhat: "Makes retrieval and agent grounding reliable without per-project plumbing. Specify them in Phase C and treat them as architecture artefacts.",
    horizon: "Now",
    glyph: "database",
    accent: "#6f9fbe",
  },
  {
    id: "evaluation-ops",
    name: "Evaluation as a release gate",
    ring: "adopt",
    angle: 128,
    quadrant: "Assurance",
    body: "Golden datasets, behavioural regression suites and human-review sampling built into the pipeline.",
    sowhat: "The single highest-leverage control for AI at scale. Encode it in Phase G and make it non-negotiable for every AI service.",
    horizon: "Now",
    glyph: "target",
    accent: "#5fd4c4",
  },
  {
    id: "agent-mesh",
    name: "Multi-agent orchestration",
    ring: "trial",
    angle: 65,
    quadrant: "Models",
    body: "Specialised agents that hand off tasks to one another under a coordinating protocol, with humans at defined review points.",
    sowhat: "Promising for long, multi-step processes, but hand-off failure modes are still poorly understood. Trial it on internal, reversible work first.",
    horizon: "12–18 months",
    glyph: "rocket",
    accent: "#a98bc4",
  },
  {
    id: "policy-as-code",
    name: "Policy as executable checks",
    ring: "trial",
    angle: 210,
    quadrant: "Assurance",
    body: "Architecture and AI policy expressed as code that the delivery pipeline evaluates automatically.",
    sowhat: "Turns governance from an assertion at a gate into a measurement at every commit. Start with five rules that matter rather than a hundred that do not.",
    horizon: "12–18 months",
    glyph: "sliders",
    accent: "#e0b584",
  },
  {
    id: "unit-economics",
    name: "Per-transaction AI economics",
    ring: "adopt",
    angle: 340,
    quadrant: "Delivery",
    body: "Cost, latency and energy budgeted per transaction and enforced at the routing layer.",
    sowhat: "Without it, successful adoption becomes a budget incident. Introduce it in Phase D before scale, not after.",
    horizon: "Now",
    glyph: "pulse",
    accent: "#8292ad",
  },
  {
    id: "private-inference",
    name: "Sovereign & private inference",
    ring: "trial",
    angle: 295,
    quadrant: "Data",
    body: "Models hosted inside a jurisdictional or organisational boundary, with residency and confidentiality guarantees.",
    sowhat: "Increasingly a hard constraint rather than a preference in regulated sectors. Model it as a routing tier in the technology architecture.",
    horizon: "12–18 months",
    glyph: "server",
    accent: "#9db08a",
  },
  {
    id: "small-models",
    name: "Task-specific small models",
    ring: "assess",
    angle: 15,
    quadrant: "Models",
    body: "Distilled or fine-tuned narrow models that outperform general models on a fixed task at a fraction of the cost.",
    sowhat: "Where volume is high and the task is stable, this is the cost lever. Assess once you have the evaluation harness to prove parity.",
    horizon: "12–18 months",
    glyph: "spark",
    accent: "#d8a657",
  },
  {
    id: "on-device",
    name: "On-device inference",
    ring: "assess",
    angle: 175,
    quadrant: "Models",
    body: "Capable models running on handsets and workstations, keeping sensitive data local and working offline.",
    sowhat: "Changes the privacy calculus in field and clinical settings. Assess device fleet and update mechanics before promising it.",
    horizon: "3+ years",
    glyph: "plug",
    accent: "#c9825a",
  },
  {
    id: "digital-twins",
    name: "Enterprise digital twins",
    ring: "assess",
    angle: 250,
    quadrant: "Data",
    body: "Live, queryable models of the operating estate used to simulate change before committing to it.",
    sowhat: "Would sharpen migration sequencing considerably, but the data foundation is rarely ready. Assess against your Phase C maturity.",
    horizon: "3+ years",
    glyph: "layers",
    accent: "#6f9fbe",
  },
  {
    id: "autonomous-agents",
    name: "Fully autonomous agents",
    ring: "hold",
    angle: 100,
    quadrant: "Models",
    body: "Agents with broad tool access and no mandatory human review, operating against open-ended goals.",
    sowhat: "The accountability model does not yet exist for consequential decisions. Hold until evaluation, containment and insurance catch up.",
    horizon: "3+ years",
    glyph: "rocket",
    accent: "#c9825a",
  },
  {
    id: "model-market",
    name: "Model churn as a planning assumption",
    ring: "hold",
    angle: 225,
    quadrant: "Delivery",
    body: "Committing architecture to a single vendor's model behaviour or pricing over a multi-year horizon.",
    sowhat: "Treat models as replaceable components with an abstraction layer. Holding a bet on one provider's roadmap is not a strategy.",
    horizon: "Now",
    glyph: "sliders",
    accent: "#8292ad",
  },
];

/** Section copy. */
export const TRENDS_SUMMARY = {
  eyebrow: "Horizon",
  title: "A radar, not a prophecy",
  lede:
    "We keep a standing view of what is ready, what is promising and what is noise. Each entry carries the question that matters: what would this change about the architecture, and when?",
};

/**
 * Group trends by ring.
 *
 * @returns {Array<{ id: string, label: string, body: string, items: Trend[] }>}
 */
export function getTrendsByRing() {
  return RINGS.map((ring) => ({
    ...ring,
    items: TRENDS.filter((trend) => trend.ring === ring.id),
  }));
}
